import {
  AlternativeQueryResults,
  DeliveryChargesRequestObject,
  DeliveryOptionsEnum,
  ProductAvailability,
  ProductColorAvailability,
  ProductColorSkuAvailability,
  ProductColorVariation,
  ProductColorVariationSize,
  ProductDetail,
  ProductDetailPriceData,
  ProductDetailPriceDataForColor,
  ProductDetailPriceDataForColorSize,
  ProductLister,
  ProductPrice,
  ProductPrices,
  StringStringMap,
  ProductFeaturesComparisonFeature,
} from '@as-react/common-types';
import { isNullOrUndefined, uniqueId } from './general.util';
import { mapEdgePriceToPricesObject } from './price.util';

// function overload https://www.typescriptlang.org/docs/handbook/functions.html#overloads
export function findColor<T extends { colorId: string | number }>(colors: Array<T>, colorId: string | number): T | null;

export function findColor<T extends { colourId: string | number }>(
  colors: Array<T>,
  colorId: string | number
): T | null;

export function findColor<T extends { colorId: string | number; colourId: string | number }>(
  colors: Array<T>,
  colorId: string | number
) {
  return colors?.find(color => (color.colorId || color.colourId) === Number(colorId)) || null;
}

export function findSizeBySku<T extends { sku: string | number }>(sizes: Array<T>, sku: string): T | null;
export function findSizeBySku<T extends { skuId: string | number }>(sizes: Array<T>, sku: string): T | null;
export function findSizeBySku<T extends { sku: string | number; skuId: string | number }>(
  sizes: Array<T>,
  sku: string
): T | null {
  return sizes?.find((size: T) => (size.sku || size.skuId).toString() === sku) || null;
}

export const findSize = (sizes: Array<ProductColorVariationSize>, sizeCode: string): ProductColorVariationSize | null =>
  sizes?.find((size: ProductColorVariationSize) => size.code === sizeCode) || null;

export const setColorAvailability = (productAvailability: ProductAvailability, colorId: number) => {
  const colorAvailability = productAvailability?.colorAvailabilities?.find(item => item.colorId === colorId);
  if (isNullOrUndefined(colorAvailability)) {
    return {};
  }
  return {
    availability: colorAvailability.availability,
    alwaysVisible: colorAvailability.alwaysVisible,
  };
};

const getColorPrice = (productPrice: ProductPrice, colorId: number) => {
  return productPrice.colours && productPrice.colours.find(item => item.colourId === colorId);
};

export const setSkuPrices = (
  productPrice: ProductPrice,
  colorId: number,
  productColorVariationSize: ProductColorVariationSize
) => {
  const colorPrice = getColorPrice(productPrice, colorId);
  if (isNullOrUndefined(colorPrice)) {
    return {};
  }
  const skuPrice = colorPrice.skus.find(
    item => item.skuId.toString() === productColorVariationSize.sku && productColorVariationSize.active
  );
  if (isNullOrUndefined(skuPrice)) {
    return {};
  }
  return {
    prices: skuPrice ? mapEdgePriceToPricesObject(skuPrice) : null,
  };
};

export const setSkuAvailability = (
  productAvailability: ProductAvailability,
  colorId: number,
  productColorVariationSize: ProductColorVariationSize
) => {
  const colorAvailability = productAvailability?.colorAvailabilities?.find(item => item.colorId === colorId);
  if (isNullOrUndefined(colorAvailability)) {
    return {};
  }
  const skuAvailability = colorAvailability?.skuAvailabilities?.find(
    item => item.skuCode === productColorVariationSize.sku && productColorVariationSize.active
  );
  if (isNullOrUndefined(skuAvailability)) {
    return {};
  }
  return {
    availability: skuAvailability.availability,
    lastPieces: skuAvailability.lastPieces,
    exactAvailability: skuAvailability.exactAvailability,
  };
};

export const setSKUDynamicSavings = (
  productPrice: ProductPrice,
  colorId: number,
  productColorVariationSize: ProductColorVariationSize
) => {
  const colorPrice = getColorPrice(productPrice, colorId);
  if (isNullOrUndefined(colorPrice)) {
    return {};
  }
  const skuPrice = colorPrice.skus.find(
    item => item.skuId.toString() === productColorVariationSize.sku && productColorVariationSize.active
  );
  if (isNullOrUndefined(skuPrice)) {
    return {};
  }
  return {
    productDiscountPercentage: skuPrice.productDiscountPercentage,
    productDiscountAmount: skuPrice.productDiscountAmount,
  };
};

export const determineSelectedSku = (
  productColors: {
    sizes?: Array<{
      sku: string;
      active?: boolean;
      availability?: number;
      historicAvailable?: boolean;
      storeSourceExcluded?: boolean;
    }>;
  },
  selectedSku?: string,
  allowSelectSoldOutSizes?: boolean
) => {
  const filteredSizes = productColors.sizes?.filter(size =>
    allowSelectSoldOutSizes
      ? (size.active && size.availability! > 0) ||
        (size.active && size.availability! <= 0 && size.historicAvailable && !size.storeSourceExcluded)
      : size.active && size.availability! > 0
  );

  if (filteredSizes && filteredSizes.length === 1) {
    return filteredSizes[0]!.sku;
  }

  if (selectedSku) {
    const selectedSize = filteredSizes?.find(size => size.sku === selectedSku);

    return selectedSize && selectedSize.active && selectedSize.availability && selectedSize.availability > 0
      ? selectedSku
      : null;
  }

  return null;
};

export const checkSizesAndSetDefaultSku = (
  selectedColorId: number,
  colors: Array<ProductColorVariation>,
  selectedSku?: string,
  allowSelectSoldOutSizes?: boolean
) => {
  const selectedColor = colors.find((color: ProductColorVariation) => color.colorId === selectedColorId);

  if (!selectedColor) {
    return null;
  }

  return determineSelectedSku(selectedColor, selectedSku, allowSelectSoldOutSizes);
};

export const determineSelectedColorId = (product: ProductDetail, productColor: string) => {
  let selectedColorVariation: ProductColorVariation | { colorId: null };
  let requestedColorVariation;

  const activeAndVisibleColors = product.productColorVariations?.filter(
    (colorVariation: ProductColorVariation) => colorVariation.visible && colorVariation.active
  );
  const defaultColorVariation = product.productColorVariations?.find(
    (colorVariation: ProductColorVariation) => product.defaultColor === colorVariation.colorId
  );

  if (
    !isNullOrUndefined(productColor) &&
    !isNaN(parseInt(productColor, 10)) &&
    activeAndVisibleColors &&
    activeAndVisibleColors.length > 0
  ) {
    requestedColorVariation = activeAndVisibleColors.find(
      (colorVariation: ProductColorVariation) => parseInt(productColor, 10) === colorVariation.colorId
    );
  }

  if (requestedColorVariation) {
    selectedColorVariation = requestedColorVariation;
  } else if (defaultColorVariation && defaultColorVariation.active && defaultColorVariation.visible) {
    selectedColorVariation = defaultColorVariation;
  } else if (activeAndVisibleColors && activeAndVisibleColors.length > 0) {
    selectedColorVariation = activeAndVisibleColors[0]!;
  } else {
    selectedColorVariation = defaultColorVariation || { colorId: null }; // Select default color anyway when nothing else is active/visible
  }

  return {
    productColorVariations: [
      selectedColorVariation,
      ...product.productColorVariations!.filter(
        (productColorVariation: ProductColorVariation) =>
          productColorVariation.colorId !== selectedColorVariation.colorId
      ),
    ],
    selectedColorId: selectedColorVariation.colorId,
  };
};

const setColorPrices = (productPrice: ProductPrice, colorId: number) => {
  const colorPrice = productPrice.colours && productPrice.colours.find(item => item.colourId === colorId);
  if (isNullOrUndefined(colorPrice)) {
    return {};
  }
  return {
    hasDifferentPrices: colorPrice.colourHasDifferentPrices,
    hasDiscount:
      colorPrice.priceState === 2 ||
      colorPrice.priceState === 3 ||
      colorPrice.priceState === 4 ||
      colorPrice.priceState === 5,
    markdown: colorPrice.markdown,
    prices: mapEdgePriceToPricesObject(colorPrice),
    priceOverlay: {
      overlayCode: colorPrice.webThumbOverlayCode,
      overlayThumb: colorPrice.webThumbOverlayPicture,
    },
  };
};

const setDynamicSavings = (productPrice: ProductPrice, colorId: number) => {
  const colorPrice = productPrice.colours && productPrice.colours.find(item => item.colourId === colorId);
  if (isNullOrUndefined(colorPrice)) {
    return {};
  }
  return {
    colourDiscountPercentage: colorPrice.colourDiscountPercentage,
    colourDiscountAmount: colorPrice.colourDiscountAmount,
  };
};

export const mapProductPriceToProductColorVariations = <
  TColorVariant extends { colorId: number | null; sizes?: Array<ProductColorVariationSize> },
>(
  productColorVariations: Array<TColorVariant>,
  productPrice: ProductPrice
): Array<ProductColorVariation | (TColorVariant & { sizes: Array<ProductColorVariationSize> })> => {
  return productColorVariations.map(
    productColorVariation =>
      ({
        ...productColorVariation,
        ...setColorPrices(productPrice, productColorVariation.colorId!),
        ...setDynamicSavings(productPrice, productColorVariation.colorId!),
        sizes: productColorVariation.sizes
          ? productColorVariation.sizes.map(size => ({
              ...size,
              ...setSkuPrices(productPrice, productColorVariation.colorId!, size),
              ...setSKUDynamicSavings(productPrice, productColorVariation.colorId!, size),
            }))
          : [],
      }) as ProductColorVariation | (TColorVariant & { sizes: Array<ProductColorVariationSize> })
  );
};

export const mapProductAvailabilityToProductColorVariations = <
  TColorVariant extends { colorId: number | null; sizes?: Array<ProductColorVariationSize> },
>(
  productColorVariations: Array<TColorVariant>,
  productAvailability: ProductAvailability
): Array<ProductColorVariation | TColorVariant> => {
  return productColorVariations.map((productColorVariation: TColorVariant): ProductColorVariation | TColorVariant => {
    return {
      ...productColorVariation,
      ...setColorAvailability(productAvailability, productColorVariation.colorId!),
      sizes: productColorVariation.sizes
        ? productColorVariation.sizes.map(
            (size: ProductColorVariationSize): ProductColorVariationSize => ({
              ...size,
              ...setSkuAvailability(productAvailability, productColorVariation.colorId!, size),
            })
          )
        : [],
    };
  });
};

export const mapColorAvailability = (colorAvailability: ProductColorAvailability) => {
  if (!colorAvailability) {
    return {};
  }

  return {
    alwaysVisible: colorAvailability.alwaysVisible,
    availability: colorAvailability.availability,
  };
};

export const mapSkuAvailability = (skuAvailability: ProductColorSkuAvailability) => {
  if (!skuAvailability) {
    return {};
  }

  return {
    availability: skuAvailability.availability,
    exactAvailability: skuAvailability.exactAvailability,
    lastPieces: skuAvailability.lastPieces,
  };
};

export const mapColorPrices = (priceDataForColor: ProductDetailPriceDataForColor) => {
  if (!priceDataForColor) {
    return {};
  }

  return {
    colourDiscountAmount: priceDataForColor.discountAmount,
    colourDiscountPercentage: priceDataForColor.discountPercentage,
    hasDifferentPrices: priceDataForColor.hasDifferentPrices,
    hasDiscount: priceDataForColor.hasDiscount,
    markdown: priceDataForColor.markdown,
    prices: priceDataForColor.prices,
    priceOverlay: priceDataForColor.priceOverlay
      ? {
          overlayCode: priceDataForColor.priceOverlay.code,
          overlayThumb: priceDataForColor.priceOverlay.thumb,
          idOverlayBackground: priceDataForColor.priceOverlay.idOverlayBackground,
          overlayBackgroundCodeDescription: priceDataForColor.priceOverlay.overlayBackgroundCodeDescription,
        }
      : {},
  };
};

export const mapSkuPrices = (priceDataForColorSize: ProductDetailPriceDataForColorSize) => {
  if (!priceDataForColorSize) {
    return {};
  }

  return {
    prices: priceDataForColorSize.prices,
    productDiscountAmount: priceDataForColorSize.discountAmount,
    productDiscountPercentage: priceDataForColorSize.discountPercentage,
  };
};

const setSellPricesToZero = (prices?: ProductPrices) => {
  const sellPrice = {
    original: 0.0,
  };

  if (!prices) {
    return {};
  } else if (!prices.STD && !prices.RRP) {
    return {
      hasDiscount: true,
      prices: {
        SELL: sellPrice,
        STD: {
          ...prices.SELL,
        },
      },
    };
  }

  return {
    prices: {
      ...prices,
      SELL: sellPrice,
    },
  };
};

export const mapAllPricesToZeroForProductColorVariations = <
  TColorVariant extends { colorId: number | null; sizes: Array<ProductColorVariationSize>; prices?: ProductPrices },
>(
  productColorVariations: Array<TColorVariant>
): Array<ProductColorVariation | TColorVariant> => {
  return productColorVariations.map((color: TColorVariant) => ({
    ...color,
    ...setSellPricesToZero(color.prices),
    sizes: color.sizes
      ? color.sizes.map((size: ProductColorVariationSize) => ({
          ...size,
          ...setSellPricesToZero(size.prices),
        }))
      : [],
  }));
};

export const mergeProductPricesToProductLister = (product: ProductLister, productPrice: ProductPrice) => {
  const productColours = product.colours.map(colour => ({
    ...colour,
    sizes: colour.sizes.map(size => {
      const color = findColor(productPrice?.colours, colour.colourId);
      const skuPrice = color?.skus ? findSizeBySku(color?.skus, size.sku) : null;

      return {
        ...size,
        prices: skuPrice ? mapEdgePriceToPricesObject(skuPrice) : null,
        productDiscountPercentage: skuPrice ? skuPrice.productDiscountPercentage : null,
        productDiscountAmount: skuPrice ? skuPrice.productDiscountAmount : null,
      };
    }),
  }));

  return {
    ...product,
    colours: productColours,
    ccExcluded: productPrice.ccExcluded,
    hdExcluded: productPrice.hdExcluded,
  } as ProductLister;
};

export const mergeProductFeatures = (
  products: Array<ProductLister>,
  productsFeaturesRecord: Record<ProductLister['productId'], Array<ProductFeaturesComparisonFeature>>,
  maxProductsSize: number,
  currentProductId?: number,
  maxFeatures?: number
) => {
  if (productsFeaturesRecord && Object.keys(productsFeaturesRecord).length <= 1) {
    return products.slice(1);
  }

  const featuresMap = currentProductId
    ? new Map(productsFeaturesRecord[currentProductId]!.map(feature => [feature.featureId, feature]))
    : Object.entries(productsFeaturesRecord)
        .flatMap(([, features]) => features)
        .reduce(
          (acc, feature, index) => (maxFeatures && index > maxFeatures ? acc : acc.set(feature.featureId!, feature)),
          new Map<number, ProductFeaturesComparisonFeature>()
        );

  if (featuresMap.size <= 0) {
    return products.slice(1);
  }

  let productsFeaturesOverlap = false;
  const productsWithFeatures = products.map(product => {
    const { productId } = product;
    const existingProductFeatures = productsFeaturesRecord[productId];

    const features = Array.from(featuresMap.values()).map(defaultFeature => {
      const existingFeature = existingProductFeatures?.find(feature => feature.featureId === defaultFeature.featureId);

      if (existingFeature && productId !== currentProductId) {
        productsFeaturesOverlap = true;
      }

      return existingFeature
        ? {
            ...existingFeature,
            attributeValue:
              existingFeature.featureType === 'BOOLEAN'
                ? existingFeature.attributeValue === '1'
                : existingFeature.attributeValue,
          }
        : {
            ...defaultFeature,
            attributeValue: null,
          };
    });

    return { ...product, features };
  });

  // If there are no overlapping features with the current product, we can remove the current product from the list
  if (!productsFeaturesOverlap) {
    return products.slice(1);
  }

  let productsToRemove = productsWithFeatures.length - maxProductsSize;
  // Remove products that don't have any features in common with the current product based on maxProductsSize
  return productsWithFeatures
    .filter(product => {
      if (productsToRemove > 0 && !product.features.some(feature => feature.attributeValue)) {
        productsToRemove--;
        return false;
      }
      return true;
    })
    .slice(0, maxProductsSize);
};

export const mergeProductAvailabilityToProductLister = (
  product: ProductLister,
  productAvailability: ProductAvailability
) => {
  const productColours = product.colours.map(colour => {
    const colorAvailability =
      productAvailability?.colorAvailabilities?.find(color => color.colorId === colour.colourId) || null;

    return {
      ...colour,
      availability: colorAvailability?.availability,
      alwaysVisible: colorAvailability?.alwaysVisible,
      sizes: colour.sizes.map(size => {
        const skuAvailability = colorAvailability
          ? colorAvailability.skuAvailabilities.find(sizeAvailability => sizeAvailability.skuCode === size.sku)
          : null;

        return {
          ...size,
          availability: skuAvailability ? skuAvailability.availability : null,
          exactAvailability: skuAvailability ? skuAvailability.exactAvailability : null,
          lastPieces: skuAvailability ? skuAvailability.lastPieces : null,
        };
      }),
    };
  });

  return {
    ...product,
    availability: productAvailability.availability,
    alwaysVisible: productAvailability.alwaysVisible,
    colours: productColours,
  } as ProductLister;
};

export const mergeProductAvailabilitiesToProductLister = (
  products: Array<ProductLister>,
  productAvailabilities: Array<ProductAvailability>
): Array<ProductLister> => {
  return products.map(product => {
    const productAvailability = productAvailabilities.find(
      availability => product.productId === availability.productId
    );
    if (productAvailability) {
      return mergeProductAvailabilityToProductLister(product, productAvailability);
    }
    return product;
  });
};

export const mergeProductDetailPriceToProductLister = (
  product: ProductLister,
  productPrice: ProductDetailPriceData
) => {
  const productColours = product.colours.map(colour => ({
    ...colour,
    sizes: colour.sizes.map(size => {
      const color = findColor(productPrice?.colours, colour.colourId);
      const skuPrice = color?.sizes ? findSizeBySku(color?.sizes, size.sku) : null;

      return {
        ...size,
        prices: skuPrice ? skuPrice.prices : null,
        productDiscountPercentage: skuPrice ? skuPrice.discountPercentage : null,
        productDiscountAmount: skuPrice ? skuPrice.discountAmount : null,
      };
    }),
  }));

  return {
    ...product,
    colours: productColours,
    ccExcluded: productPrice?.fulfillment?.ccExcluded,
    hdExcluded: productPrice?.fulfillment?.hdExcluded,
  } as ProductLister;
};

export const fixProductColoursFallback = (product?: ProductLister) =>
  product && product.colours.length > 1
    ? {
        ...product,
        colourId: product?.colours.find(colour => colour.availability > 0)?.colourId || product?.colourId,
        colours: product?.colours.filter(colour => colour.availability > 0),
      }
    : product;

export const makeProductGroupObjects = (
  productGroupUniqueIds: StringStringMap,
  alternativeQueryResults?: Array<AlternativeQueryResults>
): Record<string, Array<ProductLister>> => {
  const productGroupObjects: Record<string, Array<ProductLister>> = {};

  if (productGroupUniqueIds && alternativeQueryResults) {
    Object.keys(productGroupUniqueIds).forEach((productGroupKey: string) => {
      productGroupObjects[`${productGroupKey}`] =
        alternativeQueryResults.find(item => item.alternativeQuery === productGroupUniqueIds[`${productGroupKey}`])
          ?.items ?? [];
    });
  }

  return productGroupObjects;
};

export const getProductGroupUniqueIds = (alternativeQueryResults?: Array<AlternativeQueryResults>): StringStringMap => {
  const mappedItems: Record<string, string> = {};

  if (alternativeQueryResults) {
    alternativeQueryResults.forEach(item => {
      if (item.alternativeQuery) {
        mappedItems[`${uniqueId('productGroup-')}`] = `${item.alternativeQuery}`;
      }
    });
  }

  return mappedItems;
};

export const getDeliveryCharge = (
  deliveryCharges: DeliveryChargesRequestObject,
  deliveryOption: DeliveryOptionsEnum
): number | undefined =>
  deliveryCharges?.deliveryOptions
    ?.find(option => option.name === deliveryOption)
    ?.charges.find(charge => charge.deliveryCost === 0 && charge.minValue)?.minValue;

export const isOneSizeProduct = (sizes: Array<ProductColorVariationSize>, oneSizeProductCode: string): boolean =>
  sizes?.length === 1 && sizes[0]?.code?.toLowerCase() === oneSizeProductCode.toLowerCase();

export const isOnlyOneSizeProduct = (sizes: Array<ProductColorVariationSize>): boolean =>
  sizes?.length === 1 && sizes[0]?.code?.toLowerCase() === 'one size';
